import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faThumbsUp, faBarcode, faTruck, faPlane,
    faCheck, faExclamationTriangle, faBoxOpen, faWarehouse,
    faSpinner, faSearch, faMapMarkerAlt, faBox, faCalendarAlt,
    faWeightHanging, faArrowRight
} from '@fortawesome/free-solid-svg-icons';

const PRIMARY = '#c8102e';
const PRIMARY_LIGHT = '#fbebec';
const PRIMARY_DARK = '#a00d24';

const getIconForStatus = (statusName = '') => {
    const s = statusName.toLowerCase();
    if (s.includes('booked')) return faThumbsUp;
    if (s.includes('picked') || s.includes('counter')) return faTruck;
    if (s.includes('manifested') || s.includes('scanned')) return faBarcode;
    if (s.includes('departed') || s.includes('warehouse')) return faWarehouse;
    if (s.includes('airport') || s.includes('flight') || s.includes('clearance')) return faPlane;
    if (s.includes('arrived')) return faTruck;
    if (s.includes('delivered')) return faCheck;
    if (s.includes('exception') || s.includes('failed')) return faExclamationTriangle;
    if (s.includes('out for delivery')) return faTruck;
    return faBoxOpen;
};

const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const [datePart, timePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes] = timePart.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${day} ${monthNames[parseInt(month) - 1]} ${year}, ${hour12}:${minutes} ${ampm}`;
};

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fff5f5 0%, #fff 50%, #fafafa 100%)',
        padding: '40px 20px 80px',
    },
    searchWrap: {
        maxWidth: '680px',
        margin: '0 auto 48px',
        textAlign: 'center',
    },
    heading: {
        fontSize: '2rem',
        fontWeight: '700',
        color: '#111',
        marginBottom: '6px',
        letterSpacing: '-0.5px',
    },
    subheading: {
        color: '#888',
        fontSize: '0.95rem',
        marginBottom: '28px',
    },
    inputRow: {
        display: 'flex',
        borderRadius: '50px',
        overflow: 'hidden',
        boxShadow: '0 4px 30px rgba(200,16,46,0.25)',
        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
        padding: '3px',
        gap: '3px',
    },
    inputInner: {
        display: 'flex',
        flex: 1,
    },
    input: {
        flex: 1,
        border: 'none',
        outline: 'none',
        padding: '14px 24px',
        fontSize: '1rem',
        background: '#fff',
        color: '#222',
        letterSpacing: '1px',
        borderRadius: '47px 0 0 47px',
    },
    trackBtn: {
        background: 'transparent',
        color: '#fff',
        padding: '14px 32px',
        cursor: 'pointer',
        fontWeight: '700',
        fontSize: '0.85rem',
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        userSelect: 'none',
        flexShrink: 0,
        borderRadius: '0 47px 47px 0',
    },
    errorBox: {
        maxWidth: '680px',
        margin: '0 auto 24px',
        background: '#fff5f5',
        border: `1px solid ${PRIMARY}`,
        borderRadius: '12px',
        padding: '14px 20px',
        color: PRIMARY,
        fontWeight: '600',
        fontSize: '0.9rem',
        textAlign: 'center',
    },
    summaryCard: {
        maxWidth: '860px',
        margin: '0 auto 40px',
        background: '#fff',
        borderRadius: '20px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
        overflow: 'hidden',
    },
    summaryHeader: {
        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
        padding: '24px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
    },
    awbLabel: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        marginBottom: '4px',
    },
    awbValue: {
        color: '#fff',
        fontSize: '1.5rem',
        fontWeight: '700',
        letterSpacing: '2px',
    },
    statusBadge: {
        background: '#fff',
        border: 'none',
        borderRadius: '50px',
        padding: '10px 20px',
        color: PRIMARY,
        fontSize: '0.82rem',
        fontWeight: '700',
        maxWidth: '340px',
        textAlign: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    },
    summaryBody: {
        padding: '28px 32px',
    },
    metaGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '16px',
        marginBottom: '28px',
    },
    metaItem: {
        background: '#fafafa',
        borderRadius: '12px',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    metaLabel: {
        fontSize: '0.7rem',
        color: '#aaa',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    metaValue: {
        fontSize: '0.95rem',
        fontWeight: '600',
        color: '#222',
    },
    shipperRow: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: '16px',
        alignItems: 'center',
        background: PRIMARY_LIGHT,
        borderRadius: '14px',
        padding: '20px 24px',
    },
    shipperBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    shipperLabel: {
        fontSize: '0.7rem',
        color: PRIMARY,
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        fontWeight: '700',
    },
    shipperCompany: {
        fontSize: '0.9rem',
        fontWeight: '700',
        color: '#111',
        lineHeight: '1.3',
    },
    shipperLocation: {
        fontSize: '0.82rem',
        color: '#666',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '2px',
    },
    arrowBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    arrowIcon: {
        color: PRIMARY,
        fontSize: '1.2rem',
    },
    arrowLine: {
        width: '1px',
        height: '20px',
        background: `linear-gradient(to bottom, ${PRIMARY}, transparent)`,
    },
    timelineWrap: {
        maxWidth: '860px',
        margin: '0 auto',
        position: 'relative',
        padding: '0 0 20px',
    },
    timelineTitle: {
        fontSize: '1rem',
        fontWeight: '700',
        color: '#111',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        marginBottom: '32px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
    },
    timelineLine: {
        position: 'absolute',
        left: '31px',
        top: '60px',
        bottom: '0',
        width: '2px',
        background: `linear-gradient(to bottom, ${PRIMARY}, ${PRIMARY_LIGHT})`,
        zIndex: 0,
    },
    timelineItem: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
        marginBottom: '24px',
        position: 'relative',
        zIndex: 1,
    },
    iconWrap: {
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: `0 4px 16px rgba(200,16,46,0.3)`,
        border: '3px solid #fff',
    },
    iconWrapDone: {
        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        boxShadow: '0 4px 16px rgba(34,197,94,0.3)',
    },
    eventCard: {
        flex: 1,
        background: '#fff',
        borderRadius: '16px',
        padding: '20px 24px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
        transition: 'box-shadow 0.2s',
    },
    eventCardLast: {
        border: `1px solid ${PRIMARY}`,
        boxShadow: `0 4px 24px rgba(200,16,46,0.12)`,
    },
    eventStatus: {
        fontSize: '1rem',
        fontWeight: '700',
        color: '#111',
        marginBottom: '12px',
        lineHeight: '1.3',
    },
    eventStatusLast: {
        color: PRIMARY,
    },
    timesRow: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
    },
    timeChip: {
        borderRadius: '10px',
        padding: '8px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: '1',
        minWidth: '160px',
    },
    timeChipIndia: {
        background: '#f0f4ff',
        borderLeft: `3px solid #3b82f6`,
    },
    timeChipDest: {
        background: '#fff7ed',
        borderLeft: `3px solid #f97316`,
    },
    timeChipLabel: {
        fontSize: '0.65rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    timeChipLabelIndia: { color: '#3b82f6' },
    timeChipLabelDest: { color: '#f97316' },
    timeChipValue: {
        fontSize: '0.85rem',
        fontWeight: '600',
        color: '#222',
    },
    remarksText: {
        marginTop: '10px',
        fontSize: '0.82rem',
        color: '#888',
        fontStyle: 'italic',
    },
    lastBadge: {
        display: 'inline-block',
        background: PRIMARY_LIGHT,
        color: PRIMARY,
        fontSize: '0.65rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        padding: '3px 10px',
        borderRadius: '50px',
        marginBottom: '8px',
    },
};

function Form() {
    const [shipmentData, setShipmentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trackID, setTrackID] = useState('');

    const handleTrackClick = async () => {
        if (loading) return;
        if (!trackID.trim()) { setError('Please enter a Waybill Number'); return; }
        setShipmentData(null);
        setError(null);
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/BookingTracking/track?awbillNo=${trackID}`);
            if (!response.ok) throw new Error(`Server error: ${response.status}`);
            const result = await response.json();
            if (!result || !result.trackingEvents || result.trackingEvents.length === 0) {
                setError('No tracking details found for this Waybill Number');
            } else {
                setShipmentData(result);
            }
        } catch (err) {
            setError('An error occurred while fetching tracking details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleTrackClick(); };

    const isInternational = shipmentData?.serviceType === 'International';
    const destCountry = shipmentData?.receiver?.country || 'Destination';
    const lastIndex = shipmentData?.trackingEvents?.length - 1;

    return (
        <div style={styles.page}>

            {/* Search Section */}
            <div style={styles.searchWrap}>
                <h2 style={styles.heading}>Track Your Shipment</h2>
                <p style={styles.subheading}>Enter your AWB number to get real-time tracking updates</p>
                <div style={styles.inputRow}>
                    <input
                        style={styles.input}
                        type="text"
                        placeholder="Enter AWB / Waybill Number"
                        value={trackID}
                        onChange={(e) => setTrackID(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <div style={styles.trackBtn} onClick={handleTrackClick}>
                        {loading
                            ? <FontAwesomeIcon icon={faSpinner} spin />
                            : <><FontAwesomeIcon icon={faSearch} /> Track</>
                        }
                    </div>
                </div>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            {shipmentData && (
                <>
                    {/* Summary Card */}
                    <div style={styles.summaryCard}>
                        <div style={styles.summaryHeader}>
                            <div>
                                <div style={styles.awbLabel}>Waybill Number</div>
                                <div style={styles.awbValue}>{shipmentData.awbillNo}</div>
                            </div>
                            <div style={styles.statusBadge}>{shipmentData.currentStatus}</div>
                        </div>
                        <div style={styles.summaryBody}>
                            <div style={styles.metaGrid}>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}><FontAwesomeIcon icon={faCalendarAlt} /> Booking Date</span>
                                    <span style={styles.metaValue}>{shipmentData.bookingDate?.split('-').reverse().join('-')}</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}><FontAwesomeIcon icon={faBox} /> Service</span>
                                    <span style={styles.metaValue}>{shipmentData.serviceType}</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}><FontAwesomeIcon icon={faBox} /> Shipment Type</span>
                                    <span style={styles.metaValue}>{shipmentData.shipmentType}</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}><FontAwesomeIcon icon={faBox} /> Total Qty</span>
                                    <span style={styles.metaValue}>{shipmentData.totalQty} pcs</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}><FontAwesomeIcon icon={faWeightHanging} /> Chargeable Wt.</span>
                                    <span style={styles.metaValue}>{shipmentData.chargeableWeight} kg</span>
                                </div>
                            </div>

                            {/* Shipper → Receiver */}
                            <div style={styles.shipperRow}>
                                <div style={styles.shipperBox}>
                                    <span style={styles.shipperLabel}>From</span>
                                    <span style={styles.shipperLocation}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: PRIMARY, fontSize: '0.8rem' }} />
                                        {isInternational ? shipmentData.shipper?.country : shipmentData.shipper?.city}
                                    </span>
                                </div>
                                <div style={styles.arrowBox}>
                                    <div style={styles.arrowLine}></div>
                                    <FontAwesomeIcon icon={faArrowRight} style={styles.arrowIcon} />
                                    <div style={styles.arrowLine}></div>
                                </div>
                                <div style={{ ...styles.shipperBox, textAlign: 'right', alignItems: 'flex-end' }}>
                                    <span style={styles.shipperLabel}>To</span>
                                    <span style={styles.shipperLocation}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: PRIMARY, fontSize: '0.8rem' }} />
                                        {isInternational ? shipmentData.receiver?.country : shipmentData.receiver?.city}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div style={styles.timelineWrap}>
                        <div style={styles.timelineTitle}>
                            <span style={{ width: '40px', height: '2px', background: PRIMARY, display: 'inline-block' }}></span>
                            Tracking History
                            <span style={{ width: '40px', height: '2px', background: PRIMARY, display: 'inline-block' }}></span>
                        </div>
                        <div style={styles.timelineLine}></div>
                        {shipmentData.trackingEvents.map((event, index) => {
                            const isLast = index === lastIndex;
                            const isDone = event.statusName.toLowerCase().includes('delivered');
                            return (
                                <div key={index} style={styles.timelineItem}>
                                    <div style={{ ...styles.iconWrap, ...(isDone ? styles.iconWrapDone : {}) }}>
                                        <FontAwesomeIcon
                                            icon={getIconForStatus(event.statusName)}
                                            style={{ color: '#fff', fontSize: '22px' }}
                                        />
                                    </div>
                                    <div style={{ ...styles.eventCard, ...(isLast ? styles.eventCardLast : {}) }}>
                                        {isLast && <span style={styles.lastBadge}>● Latest Update</span>}
                                        <div style={{ ...styles.eventStatus, ...(isLast ? styles.eventStatusLast : {}) }}>
                                            {event.statusName}
                                        </div>
                                        <div style={styles.timesRow}>
                                            <div style={{ ...styles.timeChip, ...styles.timeChipIndia }}>
                                                <span style={{ ...styles.timeChipLabel, ...styles.timeChipLabelIndia }}>
                                                    🇮🇳 India Time
                                                </span>
                                                <span style={styles.timeChipValue}>{formatDateTime(event.originDateTime)}</span>
                                            </div>
                                            {event.destinationDateTime && shipmentData?.serviceType !== 'Domestic' && (
                                                <div style={{ ...styles.timeChip, ...styles.timeChipDest }}>
                                                    <span style={{ ...styles.timeChipLabel, ...styles.timeChipLabelDest }}>
                                                        📍 {destCountry} Time
                                                    </span>
                                                    <span style={styles.timeChipValue}>{formatDateTime(event.destinationDateTime)}</span>
                                                </div>
                                            )}
                                        </div>
                                        {event.remarks && <p style={styles.remarksText}>{event.remarks}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default Form;
